import { validateContentBeforeSave } from "@/lib/contentLint";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { PortalPage } from "@/components/portal/PortalPage";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { assertWritable } from "@/lib/viewAs";

export const Route = createFileRoute("/admin/announcements")({
  head: () => ({ meta: [{ title: "Announcements — Command Center" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Announcements" nav={ADMIN_NAV} tourKey="admin_tour">
        <PortalPage eyebrow="Comms" title="Announcements" intro="Compose announcements for clients, strategists, admins, or everyone. Published announcements appear in the matching audience's portal immediately.">
          <AnnouncementsManager />
        </PortalPage>
      </PortalShell>
    </ProtectedRoute>
  ),
});

type Audience = "all" | "client" | "agent" | "admin";
interface Row {
  id: string;
  title: string;
  body: string;
  audience: Audience;
  published: boolean;
  created_at: string;
  author_id: string | null;
}

const AUD_LABEL: Record<Audience, string> = {
  all: "Everyone",
  client: "Clients",
  agent: "Strategists",
  admin: "Admins",
};

function AnnouncementsManager() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [editing, setEditing] = useState<Row | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<Audience>("all");
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data, error } = await supabase
      .from("announcements")
      .select("id, title, body, audience, published, created_at, author_id")
      .order("created_at", { ascending: false });
    if (error) return toast.error(error.message);
    setRows((data ?? []) as Row[]);
  }
  useEffect(() => { void load(); }, []);

  function startNew() {
    setEditing(null);
    setTitle("");
    setBody("");
    setAudience("all");
  }
  function startEdit(r: Row) {
    setEditing(r);
    setTitle(r.title);
    setBody(r.body);
    setAudience(r.audience);
  }

  async function save(publish: boolean) {
    if (!assertWritable()) return;
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    const compliance = validateContentBeforeSave({ title: title, excerpt: null, body_md: body });
    if (compliance.blocked) {
      compliance.messages.slice(0, 3).forEach((m) => toast.error(m));
      return;
    }
    setSaving(true);
    const payload = { title: title.trim(), body: body.trim(), audience, published: publish, author_id: user?.id ?? null };
    const { data, error } = editing
      ? await supabase.from("announcements").update(payload).eq("id", editing.id).select().single()
      : await supabase.from("announcements").insert(payload).select().single();
    setSaving(false);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id,
      action: editing ? "announcement.updated" : "announcement.created",
      target_type: "announcement",
      target_id: data?.id ?? null,
      metadata: { audience, published: publish, title: payload.title.slice(0, 80) },
    });
    toast.success(publish ? "Published" : "Saved as draft");
    startNew();
    void load();
  }

  async function togglePublish(r: Row) {
    if (!assertWritable()) return;
    const next = !r.published;
    const { error } = await supabase.from("announcements").update({ published: next }).eq("id", r.id);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id,
      action: next ? "announcement.published" : "announcement.unpublished",
      target_type: "announcement",
      target_id: r.id,
    });
    toast.success(next ? "Published" : "Unpublished");
    void load();
  }

  async function remove(r: Row) {
    if (!assertWritable()) return;
    if (!confirm(`Delete announcement "${r.title}"?`)) return;
    const { error } = await supabase.from("announcements").delete().eq("id", r.id);
    if (error) return toast.error(error.message);
    await supabase.from("activity_logs").insert({
      user_id: user?.id, action: "announcement.deleted", target_type: "announcement", target_id: r.id,
    });
    toast.success("Deleted");
    if (editing?.id === r.id) startNew();
    void load();
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <div className="bg-white border border-bronze-600/20 rounded-[4px] p-6 shadow-soft">
        <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">
          {editing ? "Edit announcement" : "New announcement"}
        </p>
        <h3 className="mt-2 font-display text-navy-800 text-[22px]">
          {editing ? editing.title : "Compose"}
        </h3>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
              placeholder="A brief headline…" className="mt-1 border-bronze-600/30" />
          </div>
          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Audience</Label>
            <Select value={audience} onValueChange={(v) => setAudience(v as Audience)}>
              <SelectTrigger className="mt-1 border-bronze-600/30 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="client">Clients only</SelectItem>
                <SelectItem value="agent">Strategists only</SelectItem>
                <SelectItem value="admin">Admins only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">Body (markdown supported)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
              placeholder="Share the news, an update, or a reminder…"
              className="mt-1 border-bronze-600/30 font-sans text-[14px] leading-relaxed" />
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <PrimaryButton onClick={() => void save(true)} disabled={saving}>
              {saving ? "Saving…" : editing?.published ? "Update & keep published" : "Publish"}
            </PrimaryButton>
            <SecondaryButton onClick={() => void save(false)} disabled={saving}>
              Save draft
            </SecondaryButton>
            {editing && (
              <button onClick={startNew} className="font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500 hover:text-navy-800">
                Cancel edit
              </button>
            )}
          </div>
          <p className="font-sans text-[11px] text-charcoal-500">
            Published announcements appear in the matching audience's portal feed immediately. Email broadcast is queued for a later release.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <p className="font-sans text-[10px] uppercase tracking-[0.22em] text-bronze-600">All announcements · {rows.length}</p>
        </div>
        <div className="mt-3 bg-white border border-bronze-600/20 rounded-[4px] shadow-soft divide-y divide-bronze-600/15">
          {rows.length === 0 && (
            <div className="px-5 py-10 text-center font-sans text-[13px] text-charcoal-500">
              No announcements yet. Compose your first on the left.
            </div>
          )}
          {rows.map((r) => (
            <article key={r.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="font-display text-navy-800 text-[16px] truncate">{r.title}</h4>
                  <p className="mt-1 font-sans text-[11px] text-charcoal-500">
                    {new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {" · "}{AUD_LABEL[r.audience]}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 font-sans text-[10px] uppercase tracking-[0.14em] ${
                  r.published ? "bg-success/15 text-success" : "bg-charcoal-500/15 text-charcoal-500"
                }`}>
                  {r.published ? "Published" : "Draft"}
                </span>
              </div>
              <p className="mt-2 font-sans text-[12.5px] text-charcoal-900 line-clamp-3 whitespace-pre-wrap">{r.body}</p>
              <div className="mt-3 flex flex-wrap gap-3">
                <button onClick={() => startEdit(r)} className="font-sans text-[11px] uppercase tracking-[0.16em] text-navy-800 hover:text-bronze-600">Edit</button>
                <button onClick={() => void togglePublish(r)} className="font-sans text-[11px] uppercase tracking-[0.16em] text-navy-800 hover:text-bronze-600">
                  {r.published ? "Unpublish" : "Publish"}
                </button>
                <button onClick={() => void remove(r)} className="font-sans text-[11px] uppercase tracking-[0.16em] text-danger hover:opacity-80">Delete</button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
